// Standard views, and the controls that already existed but had no buttons.
//
// The owner asked for view buttons at the top right of every 3D viewer:
// top, bottom, left, right, front, back, and Home/iso. The capability was
// already there and unused - viewer.setStandardView(name) rotates to the
// named angles and then fits the model, so "top" means the plan of the
// whole building rather than a flight to a fixed point. It had exactly ONE
// caller in the app (the align page's "top") before this.
//
// AND FOUR MORE THAT WERE NEVER MISSING, ONLY INVISIBLE. Measurement,
// section, grid and isolate all work today on every page that mounts a
// viewer, because the viewer binds M, C, G, I (and F, H, S) on WINDOW in
// its own constructor. On the tracking page they had no buttons at all, so
// they read as missing features. They are four buttons over a working
// shortcut, which is the cheapest thing in this file by a distance.
//
// DOUBLE-FIRE: a button calls the same method the key calls, and the key
// handler lives on window, so a click must not also be read as a keypress.
// It cannot be - a click is not a keydown - but the buttons deliberately do
// NOT add their own key handling for the same reason: the keys already work
// everywhere, including on pages that will never show this strip.
import React, { useCallback } from "react";
import { Tooltip } from "antd";
import resolveViewer from "./resolveViewer";

// Every view the viewer already knows. The owner asked for four; front,
// back and iso cost nothing to expose because STANDARD_VIEWS already had
// them, and leaving them out means another pass through ten files the week
// someone asks.
// Written out rather than abbreviated. A first-letter scheme collided
// twice - Bottom/Back and Isometric/Isolate - and a test caught it, but
// the deeper reason is that the owner's complaint was that he could not
// FIND these controls. Cryptic initials would answer that with a puzzle.
export const VIEWS = [
  { key: "top", label: "Top" },
  { key: "bottom", label: "Bottom" },
  { key: "front", label: "Front" },
  { key: "back", label: "Back" },
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "iso", label: "Iso" },
];





export default function ViewerViewButtons({ viewer, variant = "mdb", style = null }) {
  // THE TARGET NODE, and why it is not optional.
  //
  // onShowGrid(target) and toggleIsolate(target) toggle 'buttonEnabled' on
  // the node they are GIVEN; toggleMeasurement and toggleClipping look
  // their button up themselves by id. Calling with no argument therefore
  // flipped grid and isolate ON while nothing lit - so the control a
  // person had just clicked looked untouched, and on a page that also had
  // its own button the two disagreed about whether grid was on. Passing
  // the clicked element is the whole fix: the button that acted is the
  // button that lights.
  const call = useCallback((method, probe, target) => {
    const v = resolveViewer(viewer, probe);
    if (!v || typeof v[method] !== "function") return;
    try { v[method](target); } catch (e) { /* the model still draws */ }
  }, [viewer]);

  const setView = useCallback((name) => {
    const v = resolveViewer(viewer, "setStandardView");
    if (!v) return;
    try { v.setStandardView(name); } catch (e) { /* the model still draws */ }
  }, [viewer]);

  // Home is the same door the H key and the Home button use, so the three
  // can never drift: it frames the whole building from the front.
  const home = useCallback(() => {
    const v = resolveViewer(viewer, "onInitialPosition");
    if (!v) return;
    try { v.onInitialPosition(); } catch (e) { /* the model still draws */ }
  }, [viewer]);

  // Inline, like the gear beside it (ViewerEffectsButton) - these controls
  // sit on pages with four different toolbars and no shared stylesheet
  // reaches all of them.
  // THE SAME VARIANTS THE GEAR HAS. It handled "floating" and this did
  // not, so on the three pages that pass it - AlignSheet, CreateIssuePage,
  // ModelStage - the strip fell through to the light default and sat
  // beside a round, shadowed gear looking like it came from another
  // product.
  const dark = variant === "dark";
  const floating = variant === "floating";
  const btn = {
    border: dark ? "1px solid #3a3a3a" : "1px solid rgba(0,0,0,.12)",
    background: dark ? "#1f1f1f" : "#fff",
    color: dark ? "#e6e6e6" : "#262626",
    borderRadius: floating ? 14 : 4,
    padding: floating ? "4px 10px" : "2px 7px",
    fontSize: 11.5,
    lineHeight: 1.6,
    cursor: "pointer",
    boxShadow: floating ? "0 2px 6px rgba(0,0,0,.18)" : undefined,
  };
  // THE STRIP NEVER POSITIONS ITSELF, exactly like the gear beside it: the
  // pages put these controls in four different toolbars, and one of them
  // holds them in a container it has already positioned. Absolute
  // positioning here would tear the strip out of that container on the
  // three pages that float.
  const wrap = { display: "inline-flex", gap: 4, flexWrap: "wrap", alignItems: "center" };

  return (
    <div style={{ ...wrap, ...(style || {}) }} className="bl-viewbtns">
      {VIEWS.map((v) => (
        <Tooltip key={v.key} title={`${v.label} view`}>
          <button type="button" style={btn} onClick={() => setView(v.key)}>
            {v.label}
          </button>
        </Tooltip>
      ))}
      <Tooltip title="Home - the whole building (H)">
        <button type="button" style={btn} onClick={home}>Home</button>
      </Tooltip>
      {/* THE TOOLS LEFT THIS STRIP on 2026-09-23. Grid, measure,
          section, isolate, hide and unhide are now rendered by
          components/Viewer/ViewerToolbar with the model view's own
          icons, on every 3D page, because the owner asked for one
          appearance rather than two for the same commands. Rendering
          them here as well would put each command on the page TWICE -
          and worse, would duplicate the ids the key handlers reach
          for. This strip keeps the named VIEWS and Home, which have no
          icon counterpart. */}
    </div>
  );
}
